import React, { useState, useRef, useEffect, useMemo } from 'react';
import './Properties.css';
import Icon from './Icon';
import { scopedKey } from './scope';
import { matchContact } from './nameMatch';
import { confirmDiscard } from './confirmDiscard';
import { putScoped } from './store/syncState';
import ImportHelp from './ImportHelp';
import { api } from './api';
import SalesMap from './SalesMap';

const ROLES = {
  buyer:   { label: "Buyer's Agent",  badge: 'buyer'   },
  listing: { label: 'Listing Agent',  badge: 'listing' },
};

// Lifecycle status. "Sold" is derived from a sold date; the rest are set manually.
const PROP_STATUS = {
  incomplete: { label: 'Incomplete' },   // blue
  active:     { label: 'Active' },        // green (listed in MLS)
  pending:    { label: 'Pending' },       // orange (under contract)
  sold:       { label: 'Sold' },          // red (has a sold date)
};
const MANUAL_STATUSES = ['incomplete', 'active', 'pending'];
function propStatus(p) {
  if (p && p.soldDate) return 'sold';
  return MANUAL_STATUSES.includes(p && p.status) ? p.status : 'incomplete';
}
// Normalized status for FILTERING and map pins — collapses the varied raw MLS strings
// ("Active", "Pending Inspection", …) into active/pending/sold so the status filter and
// pin colors work on imported data. (propStatus keeps its literal value for the badge.)
function normStatus(p) {
  if (p && p.soldDate) return 'sold';
  const s = String((p && p.status) || '').toLowerCase();
  if (s.includes('pending')) return 'pending';
  if (s.includes('active')) return 'active';
  return propStatus(p);
}

// Identity key for de-duplicating imported properties: MLS/Listing number if
// present, else the address + sold date — both scoped to the agent's role.
function propKey(p) {
  const role = (p.role || '').toString().toLowerCase().trim();
  const ln = (p.listingNumber || '').toString().toLowerCase().trim();
  if (ln) return `ln:${role}:${ln}`;
  const parts = [p.address, p.city, p.state, p.zip, p.soldDate].map(s => (s || '').toString().toLowerCase().trim());
  return parts.some(Boolean) ? `a:${role}:${parts.join('|')}` : null;
}

// Quote-aware CSV parser that tolerates commas/newlines inside quoted fields.
function parseCSVRows(text) {
  const rows = [];
  let row = [], cur = '', inQuote = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuote) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cur += '"'; i++; }
        else inQuote = false;
      } else cur += ch;
    } else if (ch === '"') {
      inQuote = true;
    } else if (ch === ',') {
      row.push(cur); cur = '';
    } else if (ch === '\r') {
      // ignore
    } else if (ch === '\n') {
      row.push(cur); rows.push(row); row = []; cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur !== '' || row.length > 0) { row.push(cur); rows.push(row); }
  return rows;
}

// "12/9/2021 12:00:00 AM" → "2021-12-09"
function normalizeDate(s) {
  if (!s) return '';
  const str = s.split(' ')[0].trim();
  // Already normalized (e.g. re-importing our own export): YYYY-MM-DD.
  const iso = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) { const [, y, m, d] = iso; return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`; }
  // NWMLS format: MM/DD/YYYY.
  const us = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!us) return '';
  const [, mm, dd, yyyy] = us;
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
}

function parseProperties(text, role) {
  const rows = parseCSVRows(text);
  if (rows.length < 2) return [];
  const headers = rows[0].map(h => h.trim().toLowerCase());
  const idx = name => headers.indexOf(name.toLowerCase());
  const col = {
    listingNumber: idx('Listing Number'),
    streetNumber:  idx('Street Number'),
    streetMod:     idx('Street Number Modifier'),
    streetDir:     idx('Street Direction'),
    streetName:    idx('Street Name'),
    streetSuffix:  idx('Street Suffix'),
    streetPost:    idx('Street Post Direction'),
    unit:          idx('Unit'),
    city:          idx('City'),
    state:         idx('State'),
    zip:           idx('Zip Code'),
    price:         idx('Selling Price'),
    listPrice:     idx('Listing Price'),
    soldDate:      idx('Selling Date'),
    owner:         idx('Owner Name'),
    owner2:        idx('Owner Name 2'),
    beds:          idx('Bedrooms'),
    baths:         idx('Bathrooms'),
    sqft:          idx('Square Footage'),
    status:        idx('Status'),
    roleCol:       idx('Role'),
  };
  const get = (cols, i) => (i >= 0 ? (cols[i] || '').trim() : '');
  // A unified export (from "Export Selected") carries a Role column. Honor it so the
  // same file re-imports correctly: rows keep their own role and the caller keeps only
  // the ones matching the button pressed. A normal MLS export has no Role column, so
  // every row falls back to the button's role (unchanged behavior).
  const normRole = (v) => { const s = String(v || '').toLowerCase(); if (s.includes('buy')) return 'buyer'; if (s.includes('list') || s.includes('sell')) return 'listing'; return ''; };
  return rows.slice(1).filter(r => r.length > 1).map((cols, n) => {
    const street = [
      get(cols, col.streetNumber), get(cols, col.streetMod), get(cols, col.streetDir),
      get(cols, col.streetName), get(cols, col.streetSuffix), get(cols, col.streetPost),
    ].filter(Boolean).join(' ');
    const unit = get(cols, col.unit);
    const rowRole = col.roleCol >= 0 ? (normRole(get(cols, col.roleCol)) || role) : role;
    return {
      id: `${rowRole}-${Date.now()}-${n}`,
      role: rowRole,
      listingNumber: get(cols, col.listingNumber),
      address: unit ? `${street} #${unit}` : street,
      city:   get(cols, col.city),
      state:  get(cols, col.state),
      zip:    get(cols, col.zip),
      price:  get(cols, col.price),
      listPrice: get(cols, col.listPrice),
      soldDate: normalizeDate(get(cols, col.soldDate)),
      owner1: get(cols, col.owner),
      owner2: get(cols, col.owner2),
      beds:   get(cols, col.beds),
      baths:  get(cols, col.baths),
      sqft:   get(cols, col.sqft),
      status: get(cols, col.status),
    };
  }).filter(p => p.address || p.city);
}

function fmtDate(ymd) {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
}

// The price to DISPLAY for a property: Selling Price once sold, Listing Price while
// on the market. Manual entries store whichever the edit form captured; each falls
// back to the other so an entered price always shows.
function displayPrice(p) {
  const sold = !!(p && p.soldDate);
  return sold ? (p.price || p.listPrice || '') : (p.listPrice || p.price || '');
}
function priceNum(v) {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}
function fmtPrice(v) {
  const n = priceNum(v);
  if (!n) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

// "Street, City, State Zip" as a single line.
function fullAddress(p) {
  const cityStateZip = [p.city, [p.state, p.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [p.address, cityStateZip].filter(Boolean).join(', ') || '—';
}

// Linked contacts as [{ id, name }] — falls back to the legacy import string.
function linkedList(p) {
  if (p.linkedContacts && p.linkedContacts.length) return p.linkedContacts;
  if (p.matchedContact) return p.matchedContact.split(', ').filter(Boolean).map((name, i) => ({ id: `legacy-${i}`, name }));
  return [];
}
function contactNames(p) {
  return linkedList(p).map(c => c.name).join(', ');
}

// Owner → contact fuzzy matching (first/last only, 1-char typo tolerance) lives in
// ./nameMatch so Contact import can reuse the exact same logic in reverse.

function emptyProp() {
  return { listingNumber: '', address: '', city: '', state: '', zip: '', price: '', listPrice: '', soldDate: '', role: 'listing', status: 'incomplete', linkedContacts: [] };
}

// Linked-contacts picker (up to 2), shared by the Add form and the Edit pane.
function LinkedContactsEditor({ linked, contacts, onAdd, onRemove }) {
  const available = contacts.filter(c => !linked.some(d => String(d.id) === String(c.id)));
  return (
    <div className="props-edit-field">
      <label className="props-edit-label">Linked Contacts ({linked.length}/2)</label>
      <div className="props-edit-contacts-row">
        <div className="props-edit-chips">
          {linked.length ? linked.map(c => (
            <span key={c.id} className="props-edit-chip">
              {c.name}
              <button className="props-edit-chip-x" title="Remove contact" onClick={() => onRemove(c.id)}>✕</button>
            </span>
          )) : <span className="props-edit-empty">No contacts linked yet.</span>}
        </div>
        {linked.length < 2 && (
          available.length ? (
            <select className="props-edit-select" value=""
              onChange={e => { const c = contacts.find(x => String(x.id) === e.target.value); if (c) onAdd(c); }}>
              <option value="">+ Add a contact…</option>
              {available.map(c => (
                <option key={c.id} value={c.id}>{`${c.firstName} ${c.lastName}`.trim()}</option>
              ))}
            </select>
          ) : <span className="props-edit-empty">No more contacts available to add.</span>
        )}
      </div>
    </div>
  );
}

// Editable property fields, shared by the Add form and the Edit pane.
function PropertyFields({ draft, onChange }) {
  const set = k => e => onChange(k, e.target.value);
  return (
    <div className="props-edit-grid">
      <div className="props-edit-field">
        <label className="props-edit-label">MLS / Listing #</label>
        <input className="props-edit-input" value={draft.listingNumber || ''} onChange={set('listingNumber')} />
      </div>
      <div className="props-edit-field props-edit-field-wide">
        <label className="props-edit-label">Address</label>
        <input className="props-edit-input" value={draft.address || ''} onChange={set('address')} placeholder="Street address" />
      </div>
      <div className="props-edit-field">
        <label className="props-edit-label">City</label>
        <input className="props-edit-input" value={draft.city || ''} onChange={set('city')} />
      </div>
      <div className="props-edit-field">
        <label className="props-edit-label">State</label>
        <input className="props-edit-input" value={draft.state || ''} onChange={set('state')} />
      </div>
      <div className="props-edit-field">
        <label className="props-edit-label">Zip Code</label>
        <input className="props-edit-input" value={draft.zip || ''} onChange={set('zip')} />
      </div>
      <div className="props-edit-field">
        <label className="props-edit-label">Sold Date</label>
        <input type="date" className="props-edit-date" value={draft.soldDate || ''} onChange={set('soldDate')} />
      </div>
      <div className="props-edit-field">
        <label className="props-edit-label">{draft.soldDate ? 'Sold Price ($)' : 'Listing Price ($)'}</label>
        <input className="props-edit-input" inputMode="numeric"
          value={(draft.soldDate ? draft.price : (draft.listPrice || draft.price)) || ''}
          onChange={set(draft.soldDate ? 'price' : 'listPrice')}
          placeholder="e.g. 550000"
          title="Selling Price once sold; Listing Price while on the market. Shown in the list and on your public profile." />
      </div>
      <div className="props-edit-field">
        <label className="props-edit-label">Status</label>
        {draft.soldDate ? (
          <span className={`props-status-badge status-sold`} title="A sold date marks this property Sold.">● Sold</span>
        ) : (
          <select className={`props-edit-select props-status-select status-${propStatus(draft)}`}
            value={propStatus(draft)} onChange={set('status')}>
            {MANUAL_STATUSES.map(s => <option key={s} value={s}>{PROP_STATUS[s].label}</option>)}
          </select>
        )}
      </div>
      <div className="props-edit-field">
        <label className="props-edit-label">Role</label>
        <select className="props-edit-select" value={draft.role || 'listing'} onChange={set('role')}>
          {Object.entries(ROLES).map(([key, r]) => <option key={key} value={key}>{r.label}</option>)}
        </select>
      </div>
    </div>
  );
}

function Properties({ scope, importEnabled = false, onOpenContact }) {
  const K_PROPERTIES = scopedKey('ira_properties', scope);
  const K_CONTACTS   = scopedKey('ira_contacts', scope);
  const buyerInputRef   = useRef(null);
  const listingInputRef = useRef(null);
  const [properties, setProperties] = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_PROPERTIES) || '[]'); } catch { return []; }
  });
  const [pending, setPending] = useState(null);   // { role, fileName, rows }
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [statusFilter, setStatusFilter] = useState('all');   // 'all' | 'active' | 'pending' | 'sold'
  const [deleteId, setDeleteId] = useState(null);
  const [sortKey, setSortKey] = useState(null);   // 'address' | 'city' | 'state' | 'zip' | 'soldDate' | 'role'
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIds, setSelectedIds]             = useState(() => new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [editProp, setEditProp] = useState(null);   // full property draft (fields + linkedContacts), committed on Save
  const [editDirty, setEditDirty] = useState(false); // unsaved changes in the open edit pane
  const [adding, setAdding]     = useState(false);
  const [addDraft, setAddDraft] = useState(emptyProp());
  const [addError, setAddError] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [contacts, setContacts] = useState([]);

  function handleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  // Persist via an explicit AWAITED, confirmed save (syncState.putScoped) — the same
  // reliable path MLS/org/agent use — not the fire-and-forget mirror iOS Safari dropped.
  // localStorage stays as the local cache for reads/hydration.
  const propsHydrated = useRef(true);
  const lastPersisted = useRef(null);   // value already saved by saveAdd's awaited put — don't re-save
  useEffect(() => {
    localStorage.setItem(K_PROPERTIES, JSON.stringify(properties));   // local cache (ira_properties is no longer auto-mirrored)
    if (propsHydrated.current) { propsHydrated.current = false; lastPersisted.current = properties; return; }
    if (properties === lastPersisted.current) return;   // saveAdd already saved this value
    putScoped(K_PROPERTIES, properties);   // server save for the other mutations: awaited + retried + drives the indicator
  }, [properties, K_PROPERTIES]);
  useEffect(() => {
    try { setContacts(JSON.parse(localStorage.getItem(K_CONTACTS) || '[]')); } catch { setContacts([]); }
  }, [K_CONTACTS]);

  // ── Map view ──────────────────────────────────────────────────────────────
  const [view, setView] = useState('list');   // 'list' | 'map'
  // Geocode any property that has an address but no cached coordinates yet (backfill
  // + newly imported/added rows). Results are stored ON the property (lat/lng, or
  // noGeo:true for unmatchable addresses) so each address is geocoded at most once.
  // geoAttempted stops same-session retries when the service is down/offline.
  const geoAttempted = useRef(new Set());
  useEffect(() => {
    const need = properties.filter(p => (p.address || p.city)
      && !Number.isFinite(p.lat) && !p.noGeo && !geoAttempted.current.has(p.id));
    if (!need.length) return;
    need.forEach(p => geoAttempted.current.add(p.id));
    // NOTE: no cancellation on re-run — hydration/reconcile can replace `properties`
    // while the (multi-second) Census call is in flight, and cancelling here would
    // discard the results while geoAttempted blocks a retry. The functional update
    // below merges safely into whatever the latest state is.
    (async () => {
      const merged = {};
      try {
        for (let i = 0; i < need.length; i += 800) {   // endpoint caps at 1000/request
          const items = need.slice(i, i + 800).map(p => ({
            id: String(p.id), street: p.address || '', city: p.city || '', state: p.state || '', zip: p.zip || '',
          }));
          const d = await api.post('/api/geocode', { items });
          Object.assign(merged, d?.results || {});
        }
      } catch { return; }   // offline / local mode — retried next session
      if (!Object.keys(merged).length) return;
      setProperties(prev => prev.map(p => {
        const r = merged[String(p.id)];
        if (r === undefined) return p;
        return r ? { ...p, lat: r.lat, lng: r.lng, noGeo: false } : { ...p, noGeo: true };
      }));
    })();
  }, [properties]);

  async function openEdit(p) {
    // Switching to another property's editor abandons the current draft — confirm first.
    if (!(await confirmDiscard(editDirty))) return;
    setAdding(false);
    setEditId(p.id);
    setEditProp({
      listingNumber: p.listingNumber || '', address: p.address || '', city: p.city || '',
      state: p.state || '', zip: p.zip || '', price: p.price || '', listPrice: p.listPrice || '', soldDate: p.soldDate || '', role: p.role || 'listing',
      // Without status in the draft the pane's dropdown always displayed "Incomplete",
      // even for a saved Active/Pending listing (the save was fine — the display lied).
      status: p.status || '',
      linkedContacts: linkedList(p).map(c => ({ id: c.id, name: c.name })),
    });
    setEditDirty(false);
  }
  // Pure reset — no guard. Used after Save and by the guarded exit paths.
  function resetEdit() { setEditId(null); setEditProp(null); setEditDirty(false); }
  async function closeEdit() {
    if (!(await confirmDiscard(editDirty))) return;
    resetEdit();
  }
  function updateEditField(k, v) { setEditProp(prev => ({ ...prev, [k]: v })); setEditDirty(true); }
  function addDraftContact(contact) {
    setEditProp(prev => {
      const linked = prev.linkedContacts || [];
      if (linked.length >= 2 || linked.some(c => String(c.id) === String(contact.id))) return prev;
      return { ...prev, linkedContacts: [...linked, { id: contact.id, name: `${contact.firstName} ${contact.lastName}`.trim() }] };
    });
    setEditDirty(true);
  }
  function removeDraftContact(id) {
    setEditProp(prev => ({ ...prev, linkedContacts: (prev.linkedContacts || []).filter(c => String(c.id) !== String(id)) }));
    setEditDirty(true);
  }
  function saveEdit() {
    setProperties(prev => prev.map(p => {
      if (p.id !== editId) return p;
      const next = { ...p, ...editProp };
      // Address changed → drop the cached coordinates so the map effect re-geocodes.
      if (['address', 'city', 'state', 'zip'].some(k => (p[k] || '') !== (next[k] || ''))) {
        delete next.lat; delete next.lng; delete next.noGeo;
        geoAttempted.current.delete(p.id);
      }
      return next;
    }));
    resetEdit();
  }

  async function startAdd() {
    if (!(await confirmDiscard(editDirty))) return;
    resetEdit(); setAddDraft(emptyProp()); setAddError(''); setAdding(true);
  }
  function cancelAdd() { setAdding(false); setAddError(''); }
  function updateAddField(k, v) { setAddDraft(prev => ({ ...prev, [k]: v })); setAddError(''); }
  function addAddContact(contact) {
    setAddDraft(prev => {
      const linked = prev.linkedContacts || [];
      if (linked.length >= 2 || linked.some(c => String(c.id) === String(contact.id))) return prev;
      return { ...prev, linkedContacts: [...linked, { id: contact.id, name: `${contact.firstName} ${contact.lastName}`.trim() }] };
    });
  }
  function removeAddContact(id) {
    setAddDraft(prev => ({ ...prev, linkedContacts: (prev.linkedContacts || []).filter(c => String(c.id) !== String(id)) }));
  }
  async function saveAdd() {
    if (!addDraft.address && !addDraft.city && !addDraft.listingNumber) return;
    const key = propKey(addDraft);
    if (key && properties.some(p => propKey(p) === key)) {
      setAddError(addDraft.listingNumber
        ? 'A property with this MLS/Listing # already exists for this role.'
        : 'A property with this address and sold date already exists for this role.');
      return;
    }
    const next = [...properties, { id: `manual-${Date.now()}`, ...addDraft }];
    // Save to the server FIRST and WAIT for confirmation — exactly like the Add Agent
    // form. The property only "lands" once persisted; on failure the form stays open.
    setAddError(''); setAddSaving(true);
    const ok = await putScoped(K_PROPERTIES, next);
    setAddSaving(false);
    if (!ok) { setAddError('Could not save the property — check your connection and try again.'); return; }
    lastPersisted.current = next;        // already saved → the persist effect won't re-send it
    setProperties(next);
    setAdding(false);
  }

  function handleFile(role, e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setError('');
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseProperties(ev.target.result, role);
      if (parsed.length === 0) {
        setError('No properties found. Make sure this is the correct SOLD export (with Street Name, City, Selling Price, Selling Date columns).');
        return;
      }
      // A unified export (with a Role column) is split by side: the Buyers button
      // imports only buyer-side rows, the Sellers button only listing-side rows. A
      // normal MLS export (no Role column) already tags every row with this role.
      let rows = parsed.filter(p => p.role === role);
      if (rows.length === 0) {
        setError(role === 'buyer'
          ? 'This file has no buyer-side sales to import — its rows are listing-side. Use the “Sellers” import for those.'
          : 'This file has no listing-side sales to import — its rows are buyer-side. Use the “Buyers” import for those.');
        return;
      }
      // Listing-agent sales: fuzzy-match owners to existing contacts (ignoring middle names).
      if (role === 'listing') {
        let contacts = [];
        try { contacts = JSON.parse(localStorage.getItem(K_CONTACTS) || '[]'); } catch { contacts = []; }
        rows = rows.map(p => {
          const matches = [];
          for (const owner of [p.owner1, p.owner2]) {
            const m = matchContact(owner, contacts);
            if (m && !matches.some(x => x.id === m.id)) matches.push(m);
          }
          return {
            ...p,
            linkedContacts: matches.slice(0, 2).map(m => ({ id: m.id, name: `${m.firstName} ${m.lastName}`.trim() })),
          };
        });
      }
      // Drop properties already present (or repeated within the file) so importing
      // the same file twice never creates duplicates. Match on MLS/Listing number
      // when available, otherwise on the address + sold date (scoped to the role).
      const seen = new Set(properties.map(propKey).filter(Boolean));
      const unique = [];
      let dupes = 0;
      for (const p of rows) {
        const k = propKey(p);
        if (k && seen.has(k)) { dupes++; continue; }
        if (k) seen.add(k);
        unique.push(p);
      }
      setPending({ role, fileName: file.name, rows: unique, dupes });
    };
    reader.readAsText(file);
  }

  function confirmImport() {
    setProperties(prev => [...prev, ...pending.rows]);
    setPending(null);
  }
  function cancelImport() { setPending(null); setError(''); }

  // The inline confirmation renders in a row below the target — if that row sits at
  // the bottom edge of the screen, nudge the confirmation into view so it's never missed.
  useEffect(() => {
    // setTimeout (not requestAnimationFrame): rAF can stall in hidden/background tabs.
    if (deleteId) setTimeout(() => {
      document.querySelector('.row-delete-notice')?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }, [deleteId]);

  function executeDelete() {
    setProperties(prev => prev.filter(p => p.id !== deleteId));
    setSelectedIds(prev => { const next = new Set(prev); next.delete(deleteId); return next; });
    setDeleteId(null);
  }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleSelectAll() {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = displayed.length > 0 && displayed.every(p => next.has(p.id));
      displayed.forEach(p => allSelected ? next.delete(p.id) : next.add(p.id));
      return next;
    });
  }
  function executeBulkDelete() {
    setProperties(prev => prev.filter(p => !selectedIds.has(p.id)));
    setSelectedIds(new Set());
    setBulkDeleteConfirm(false);
  }

  // Export the selected properties to a .csv. Headers mirror the importer's column
  // names (address goes in "Street Name") so the file re-imports cleanly; a trailing
  // Role column is for reference (role is chosen by the import button).
  function exportSelected() {
    const sel = properties.filter(p => selectedIds.has(p.id));
    if (sel.length === 0) return;
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const headers = ['Listing Number', 'Street Name', 'City', 'State', 'Zip Code', 'Selling Price', 'Listing Price', 'Selling Date', 'Owner Name', 'Owner Name 2', 'Bedrooms', 'Bathrooms', 'Square Footage', 'Status', 'Role'];
    const lines = sel.map(p => [p.listingNumber, p.address, p.city, p.state, p.zip, p.price, p.listPrice, p.soldDate, p.owner1, p.owner2, p.beds, p.baths, p.sqft, propStatus(p), p.role].map(esc).join(','));
    const csv = [headers.join(','), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `properties-export-${sel.length}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  const displayed = useMemo(() => {
    const q = search.toLowerCase().trim();
    let result = properties.filter(p => {
      if (statusFilter !== 'all' && normStatus(p) !== statusFilter) return false;
      if (!q) return true;
      return fullAddress(p).toLowerCase().includes(q) || contactNames(p).toLowerCase().includes(q);
    });
    if (sortKey) {
      result.sort((a, b) => {
        // Price sorts numerically on the DISPLAYED price (listing vs selling by status).
        if (sortKey === 'price') {
          const an = priceNum(displayPrice(a)), bn = priceNum(displayPrice(b));
          if (!an && !bn) return 0;
          if (!an) return 1;        // blanks last
          if (!bn) return -1;
          return sortDir === 'asc' ? an - bn : bn - an;
        }
        const av = (a[sortKey] || '').toString();
        const bv = (b[sortKey] || '').toString();
        if (!av && !bv) return 0;
        if (!av) return 1;          // blanks last
        if (!bv) return -1;
        const cmp = sortKey === 'soldDate'
          ? av.localeCompare(bv)    // ISO 'YYYY-MM-DD' sorts chronologically
          : av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [properties, search, statusFilter, sortKey, sortDir]);

  function SortIcon({ k }) {
    if (sortKey !== k) return <span className="sort-icon unsorted">⇅</span>;
    return <span className="sort-icon active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  const allDisplayedSelected = displayed.length > 0 && displayed.every(p => selectedIds.has(p.id));
  const someDisplayedSelected = displayed.some(p => selectedIds.has(p.id));

  return (
    <div className="props-container">
      <div className="props-header">
        <div>
          <h2>Properties</h2>
          <p>Import the homes you've closed, by your role on the deal.</p>
        </div>
        <div className="props-header-btns">
          <button className="props-import-btn props-add-btn" onClick={startAdd}>
            <Icon name="add" size={16} /><span className="lbl-full">Add Property</span><span className="lbl-short">Add</span>
          </button>
          {importEnabled && (
            <>
              <button className="props-import-btn props-import-buyer" onClick={() => buyerInputRef.current.click()}>
                <Icon name="import" size={16} /> <span className="lbl-full">Import Buyer's Agent Sales</span><span className="lbl-short">Buyers</span>
              </button>
              <ImportHelp type="buyer" />
              <button className="props-import-btn props-import-listing" onClick={() => listingInputRef.current.click()}>
                <Icon name="import" size={16} /> <span className="lbl-full">Import Listing Agent Sales</span><span className="lbl-short">Sellers</span>
              </button>
              <ImportHelp type="listing" />
            </>
          )}
        </div>
        <input ref={buyerInputRef}   type="file" accept=".csv" style={{ display: 'none' }} onChange={e => handleFile('buyer', e)} />
        <input ref={listingInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => handleFile('listing', e)} />
      </div>

      {!importEnabled && (
        <div className="props-mls-hint"><Icon name="lock" size={16} /> Select a Supported MLS in your <strong>Profile</strong> to enable importing properties.</div>
      )}

      {error && <div className="props-error">⚠ {error}</div>}

      {adding && (
        <div className="props-add-panel">
          <div className="props-add-title">New Property</div>
          {addError && <div className="props-error">⚠ {addError}</div>}
          <PropertyFields draft={addDraft} onChange={updateAddField} />
          <LinkedContactsEditor linked={addDraft.linkedContacts || []} contacts={contacts}
            onAdd={addAddContact} onRemove={removeAddContact} />
          <div className="props-edit-actions">
            <button className="rt-save-btn" onClick={saveAdd} disabled={addSaving}>{addSaving ? 'Saving…' : 'Save Property'}</button>
            <button className="contacts-import-cancel-btn" onClick={cancelAdd}>Cancel</button>
          </div>
        </div>
      )}

      {pending && (
        <div className="props-import-notice">
          <span>
            <Icon name="files" size={16} /> <strong>{pending.fileName}</strong> — {pending.rows.length} propert{pending.rows.length !== 1 ? 'ies' : 'y'}
            {' '}as <strong>{ROLES[pending.role].label}</strong> ready to import.
            {pending.dupes > 0 && <span className="props-import-dupes"> {pending.dupes} duplicate{pending.dupes !== 1 ? 's' : ''} skipped.</span>}
          </span>
          {pending.rows.length > 0
            ? <button className="props-confirm-btn" onClick={confirmImport}>Confirm Import</button>
            : <span className="props-import-dupes">Nothing new to import.</span>}
          <button className="contacts-import-cancel-btn" onClick={cancelImport}>Cancel</button>
        </div>
      )}

      {bulkDeleteConfirm && (
        <div className="contacts-delete-notice">
          <span><Icon name="delete" size={16} /> Delete <strong>{selectedIds.size}</strong> selected propert{selectedIds.size !== 1 ? 'ies' : 'y'}? This cannot be undone.</span>
          <button className="contacts-delete-confirm-btn" onClick={executeBulkDelete}>Delete</button>
          <button className="contacts-import-cancel-btn" onClick={() => setBulkDeleteConfirm(false)}>Cancel</button>
        </div>
      )}

      {properties.length > 0 ? (
        <>
          <div className="props-toolbar">
            <input className="props-search" type="text"
              placeholder="Search by address or contact…"
              value={search} onChange={e => setSearch(e.target.value)} />
            <select className="props-status-filter" title="Filter by status"
              value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="sold">Sold</option>
            </select>
            <span className="props-search-count">
              {(search || statusFilter !== 'all') ? `${displayed.length} of ` : ''}{properties.length} propert{properties.length !== 1 ? 'ies' : 'y'}
            </span>
            <div className="props-view-toggle" role="tablist">
              <button className={`props-view-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}><Icon name="list" size={15} /> List</button>
              <button className={`props-view-btn ${view === 'map' ? 'active' : ''}`} onClick={() => setView('map')}><Icon name="map" size={15} /> Map</button>
            </div>
          </div>

          {/* Mobile-only select-all + selected count (matches Contacts). The header
              checkbox is hidden when the table reflows into cards. */}
          <div className="contacts-mobile-selectall">
            <label className="cms-toggle">
              <input type="checkbox"
                ref={el => { if (el) el.indeterminate = someDisplayedSelected && !allDisplayedSelected; }}
                checked={allDisplayedSelected}
                onChange={toggleSelectAll} />
              <span>{allDisplayedSelected ? 'Deselect all' : 'Select all'}{displayed.length ? ` (${displayed.length})` : ''}</span>
            </label>
            {selectedIds.size > 0 && <span className="cms-count">{selectedIds.size} selected</span>}
          </div>

          {selectedIds.size > 0 && (
            <div className="contacts-bulk-bar">
              <span className="contacts-bulk-count">{selectedIds.size} selected</span>
              <button className="contacts-bulk-export-btn" onClick={exportSelected}>
                <Icon name="export" size={16} /> <span className="lbl-full">Export Selected</span><span className="lbl-short">Export</span>
              </button>
              <button className="contacts-bulk-delete-btn" onClick={() => setBulkDeleteConfirm(true)}>
                <Icon name="delete" size={16} /> <span className="lbl-full">Delete Selected</span><span className="lbl-short">Delete</span>
              </button>
            </div>
          )}

          {view === 'map' ? (() => {
            const mappable = displayed.filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
            const points = mappable.map(p => ({
              id: p.id, lat: p.lat, lng: p.lng, status: normStatus(p),
              title: p.address || p.city || p.listingNumber || 'Property',
              lines: [
                [p.city, p.state, p.zip].filter(Boolean).join(', '),
                `${ROLES[p.role]?.label || ''} · ${PROP_STATUS[propStatus(p)].label}`,
                [p.soldDate ? `Sold ${fmtDate(p.soldDate)}` : '', priceNum(displayPrice(p)) ? fmtPrice(displayPrice(p)) : ''].filter(Boolean).join(' · '),
              ].filter(Boolean),
            }));
            // Mount the map when ANY property has coordinates (unfiltered) — the live
            // search only swaps pins. Unmounting Leaflet on a filter keystroke is what
            // crashed (zoom-transition callback firing on a destroyed map).
            const anyGeo = properties.some(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
            return (
              <div className="props-map-block">
                {anyGeo
                  ? <SalesMap points={points} height={460} />
                  : <div className="props-no-results" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
                      No mappable properties yet — locations appear here shortly after an address is added or imported.
                    </div>}
                {mappable.length < displayed.length && (
                  <div className="props-map-note">
                    {displayed.length - mappable.length} propert{displayed.length - mappable.length !== 1 ? 'ies' : 'y'} without a map location
                    {' '}(address missing or not yet located).
                  </div>
                )}
              </div>
            );
          })() : (
          <div className="props-table-wrap">
            <table className="props-table">
              <thead>
                <tr>
                  <th className="col-select">
                    <input type="checkbox" title="Select all"
                      ref={el => { if (el) el.indeterminate = someDisplayedSelected && !allDisplayedSelected; }}
                      checked={allDisplayedSelected}
                      onChange={toggleSelectAll} />
                  </th>
                  <th>Listing #</th>
                  <th className="sortable" onClick={() => handleSort('address')}>Address <SortIcon k="address" /></th>
                  <th>Status</th>
                  <th className="sortable" onClick={() => handleSort('price')}>Price <SortIcon k="price" /></th>
                  <th className="sortable" onClick={() => handleSort('soldDate')}>Sold Date <SortIcon k="soldDate" /></th>
                  <th className="sortable" onClick={() => handleSort('role')}>Role <SortIcon k="role" /></th>
                  <th>Contact</th>
                  <th className="col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {displayed.length > 0 ? displayed.map(p => {
                  const linked = linkedList(p);
                  return (
                  <React.Fragment key={p.id}>
                    <tr className={`${selectedIds.has(p.id) ? 'selected-row' : ''} ${editId === p.id ? 'props-row-editing' : ''}`}>
                      <td className="col-select">
                        <input type="checkbox" checked={selectedIds.has(p.id)}
                          onChange={() => toggleSelect(p.id)} />
                      </td>
                      <td data-label="Listing #">{p.listingNumber || '—'}</td>
                      <td className="props-addr" data-label="Address">{fullAddress(p)}</td>
                      <td data-label="Status">
                        <span className={`props-status-badge status-${propStatus(p)}`}>● {PROP_STATUS[propStatus(p)].label}</span>
                      </td>
                      <td data-label="Price" title={p.soldDate ? 'Selling Price' : 'Listing Price'}>{fmtPrice(displayPrice(p))}</td>
                      <td data-label="Sold Date">{fmtDate(p.soldDate)}</td>
                      <td data-label="Role">
                        <span className={`props-role-badge ${ROLES[p.role].badge}`}>{ROLES[p.role].label}</span>
                      </td>
                      <td className="props-contact" data-label="Contact">
                        {linked.length
                          ? linked.map(c => {
                              // Only a contact that still exists is clickable — legacy
                              // import names (no real record) stay plain text.
                              const real = onOpenContact && contacts.some(x => String(x.id) === String(c.id));
                              return real ? (
                                <button key={c.id} type="button" className="props-contact-match props-contact-link"
                                  title={`Edit ${c.name}`} onClick={() => onOpenContact(c.id)}>{c.name}</button>
                              ) : (
                                <span key={c.id} className="props-contact-match">{c.name}</span>
                              );
                            })
                          : '—'}
                      </td>
                      <td className="col-actions">
                        <button className={`icon-btn edit-icon-btn ${editId === p.id ? 'active' : ''}`}
                          title="Edit property"
                          onClick={() => editId === p.id ? closeEdit() : openEdit(p)}><Icon name="rename" size={16} /></button>
                        <button className="icon-btn delete-icon-btn" title="Delete property"
                          onClick={() => setDeleteId(p.id)}><Icon name="delete" size={16} /></button>
                      </td>
                    </tr>
                    {/* Inline delete confirmation — right under the target row so it's
                        always visible where the Delete was clicked. */}
                    {deleteId === p.id && (
                      <tr className="row-delete-confirm">
                        <td colSpan={9}>
                          <div className="contacts-delete-notice row-delete-notice">
                            <span><Icon name="delete" size={16} /> Delete <strong>{p.address || p.city || p.listingNumber || 'this property'}</strong>? This cannot be undone.</span>
                            <button className="contacts-delete-confirm-btn" onClick={executeDelete}>Delete</button>
                            <button className="contacts-import-cancel-btn" onClick={() => setDeleteId(null)}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {editId === p.id && editProp && (
                      <tr className="props-edit-row">
                        <td colSpan={9}>
                          <div className="props-edit-pane">
                            <PropertyFields draft={editProp} onChange={updateEditField} />
                            <LinkedContactsEditor linked={editProp.linkedContacts || []} contacts={contacts}
                              onAdd={addDraftContact} onRemove={removeDraftContact} />
                            <div className="props-edit-actions">
                              {editDirty && <span className="assign-unsaved-hint">● Unsaved changes</span>}
                              <button className="rt-save-btn" onClick={saveEdit}>Save</button>
                              <button className="contacts-import-cancel-btn" onClick={closeEdit}>Cancel</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                }) : (
                  <tr>
                    <td colSpan={9} className="props-no-results">No properties match your search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </>
      ) : !pending && !adding && (
        <div className="props-empty">
          <span className="props-empty-icon"><Icon name="properties" size={18} /></span>
          <p>No properties yet. Add one manually, or import your buyer's-agent or listing-agent sales to get started.</p>
        </div>
      )}
    </div>
  );
}

export default Properties;
